import type { ApiResponse } from '@/types'
import type { ChatConversation, ChatFeatures, ChatMessage, ChatMessagesPage } from '@/types/chat'
import { chatHttp } from '@/lib/chat-api'

const unwrapData = <T>(payload: ApiResponse<T> | T | null | undefined): T | null => {
  if (!payload) {
    return null
  }
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload.data as T | null) ?? null
  }
  return payload as T
}

export const chatService = {
  async getFeatures(): Promise<ChatFeatures | null> {
    const response = await chatHttp.get<ApiResponse<ChatFeatures>>('/Chat/Features/')
    return unwrapData(response.data)
  },

  async getJobConversation(jobId: number | string): Promise<ChatConversation | null> {
    const response = await chatHttp.get<ApiResponse<ChatConversation>>(`/Chat/Job/${jobId}/Conversation/`)
    return unwrapData(response.data)
  },

  async listMessages(
    conversationId: string,
    params?: { limit?: number; cursor?: string | null },
  ): Promise<ChatMessagesPage> {
    const response = await chatHttp.get<ApiResponse<ChatMessagesPage>>(
      `/Chat/Conversation/${conversationId}/Messages/`,
      {
        params: {
          limit: params?.limit ?? 50,
          ...(params?.cursor ? { cursor: params.cursor } : {}),
        },
      },
    )
    return unwrapData(response.data) ?? { results: [], next_cursor: null }
  },

  async sendMessage(
    conversationId: string,
    body: string,
    clientMessageId?: string,
  ): Promise<ChatMessage | null> {
    const response = await chatHttp.post<ApiResponse<ChatMessage>>(
      `/Chat/Conversation/${conversationId}/Send/`,
      {
        body,
        ...(clientMessageId ? { client_message_id: clientMessageId } : {}),
      },
    )
    return unwrapData(response.data)
  },

  async markRead(conversationId: string, lastReadMessageId?: string): Promise<void> {
    await chatHttp.post(`/Chat/Conversation/${conversationId}/MarkRead/`, {
      ...(lastReadMessageId ? { last_read_message_id: lastReadMessageId } : {}),
    })
  },
}
